import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Shell-shaped skeleton pieces for pages built on `AutomationsWorkspaceShell`
 * (Automations, Forms, Campaigns...). Keeping them here means one set of
 * measurements for the tabs strip, the filter toolbar and the card-theme table,
 * so a page's loading state stays aligned with its real chrome.
 */

/** Network tabs strip — mirrors `AutomationsWorkspaceShell`. */
export const WorkspaceTabsSkeleton: React.FC<{ widths?: number[] }> = ({
  widths = [64, 76, 72],
}) => (
  <div className="shrink-0 border-b border-[#EAECF0] bg-white px-[15px] pt-3">
    <div className="flex items-end gap-1">
      {widths.map((width, i) => (
        <div key={i} className="inline-flex items-center gap-2 px-3 pb-3 pt-2">
          <Skeleton className="h-4 w-4 shrink-0 rounded" />
          <Skeleton className="h-4 rounded" style={{ width }} />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Filter toolbar — mirrors `AutomationsWorkspaceFilterBar`: a search field, one
 * placeholder per filter dropdown, then the trailing action buttons. The last
 * action stays visible on mobile the way the create button does.
 */
export const WorkspaceFilterBarSkeleton: React.FC<{
  filters?: number;
  actions?: number[];
}> = ({ filters = 2, actions = [168] }) => (
  <div className="flex flex-wrap items-center gap-2 bg-white px-[15px] py-3">
    <Skeleton className="h-9 min-w-0 flex-1 rounded-lg sm:w-[260px] sm:max-w-[280px] sm:flex-none" />
    {Array.from({ length: filters }).map((_, i) => (
      <Skeleton key={i} className="hidden h-9 w-[92px] rounded-lg sm:block" />
    ))}
    {actions.length > 0 ? (
      <div className="ml-auto flex items-center gap-2">
        {actions.map((width, i) => (
          <Skeleton
            key={i}
            className={cn('h-9 rounded-lg', i < actions.length - 1 && 'hidden sm:block')}
            style={{ width }}
          />
        ))}
      </div>
    ) : null}
  </div>
);

export type WorkspaceTableSkeletonColumn = {
  /** Skeleton bar width inside the cell, e.g. 'w-full' or 'w-16'. */
  width: string;
  /** Responsive visibility to match the real column, e.g. 'hidden sm:table-cell'. */
  cellClassName?: string;
  /** Right-aligned, narrow column (the actions menu). */
  actions?: boolean;
};

/**
 * Card-theme table placeholder — mirrors `DataTable variant="cardTheme"`:
 * the `rounded-2xl` shell, the `#FAFAFB` header strip and hairline rows, so
 * columns keep their widths and responsive hiding while data loads.
 */
export const WorkspaceTableSkeleton: React.FC<{
  columns: WorkspaceTableSkeletonColumn[];
  rows?: number;
  'data-testid'?: string;
}> = ({ columns, rows = 8, 'data-testid': testId }) => (
  <div
    className="overflow-hidden rounded-2xl border border-[#EAECF0] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
    aria-busy="true"
    data-testid={testId}
  >
    <table className="w-full caption-bottom text-[13px]">
      <thead className="border-b border-[#EAECF0] bg-[#FAFAFB]">
        <tr>
          {columns.map((column, i) => (
            <th
              key={i}
              className={cn(
                'border-0 bg-[#FAFAFB] px-3.5 py-3 text-left',
                column.actions && 'w-16 text-right',
                column.cellClassName,
              )}
            >
              <Skeleton className={cn('h-2.5 rounded', column.actions ? 'ml-auto w-8' : 'w-20')} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <tr key={rowIndex} className="border-b border-[#F2F4F7] last:border-b-0">
            {columns.map((column, i) => (
              <td
                key={i}
                className={cn(
                  'border-0 px-3.5 py-3.5 align-middle',
                  column.actions && 'w-16 text-right',
                  column.cellClassName,
                )}
              >
                <Skeleton
                  className={cn('h-4 rounded', column.actions ? 'ml-auto w-8' : column.width)}
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
